/* eslint-disable react/no-unused-prop-types */
import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import { Table, TableSize } from '@ui/molecules/Table';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import AddAssetExclusionsDrawer from './AddAssetExclusionsDrawer';
const namespace = 'features.settings.ai_inspection_manager.missing_information';
export default function AssetExclusionsManager({ assetExclusions, onAddAssets, onDeleteAsset, onDeleteAssets, subtitle }) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const handleDeleteAsset = useCallback((asset) => {
        setAssetToDelete(asset);
        setDeleteConfirmOpen(true);
    }, []);
    const confirmDeleteAsset = useCallback(() => {
        if (assetToDelete) {
            onDeleteAsset(assetToDelete.value);
            setDeleteConfirmOpen(false);
            setAssetToDelete(null);
        }
    }, [assetToDelete, onDeleteAsset]);
    const handleSelectionChange = useCallback((assetValue, checked) => {
        const key = String(assetValue);
        setSelectedAssets((prev) => {
            if (checked) {
                return [...prev, key];
            }
            return prev.filter((value) => value !== key);
        });
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        if (selectAll) {
            setSelectedAssets(assetExclusions.map((asset) => asset.value));
        }
        else {
            setSelectedAssets([]);
        }
    }, [assetExclusions]);
    const handleBulkDelete = useCallback(() => {
        if (selectedAssets.length > 0) {
            setBulkDeleteConfirmOpen(true);
        }
    }, [selectedAssets]);
    const confirmBulkDelete = useCallback(() => {
        onDeleteAssets(selectedAssets);
        setBulkDeleteConfirmOpen(false);
        setSelectedAssets([]);
    }, [selectedAssets, onDeleteAssets]);
    const handleAddAssets = useCallback((assetTypeValues) => {
        onAddAssets(assetTypeValues);
        setIsDrawerOpen(false);
    }, [onAddAssets]);
    const assetColumns = useMemo(() => [
        {
            dataKey: 'label',
            label: translate('asset', { namespace }),
            columnType: 'primary',
            width: '100%'
        },
        {
            dataKey: 'actions',
            label: translate('actions', { namespace }),
            width: '1px',
            renderer: ({ item }) => (React.createElement(Button, { onClick: () => handleDeleteAsset(item) },
                React.createElement("i", { className: "fa-light fa-trash-can" })))
        }
    ], [handleDeleteAsset]);
    return (React.createElement("div", null,
        React.createElement("div", { className: `missing-information-tab__actions ${selectedAssets.length > 0 ? 'missing-information-tab__actions--with-selection' : ''}` },
            selectedAssets.length > 0 && (React.createElement(Button, { onClick: handleBulkDelete },
                React.createElement("i", { className: "fa-light fa-trash-can" }),
                " ",
                translate('delete_selected', { namespace }),
                " (",
                selectedAssets.length,
                ")")),
            React.createElement(Button, { onClick: () => setIsDrawerOpen(true) },
                React.createElement("i", { className: "fa-light fa-plus" }),
                " ",
                translate('add_assets', { namespace }))),
        assetExclusions.length > 0 ? (React.createElement(Table, { columns: assetColumns, data: assetExclusions, size: TableSize.Full, variant: "detail", selectable: true, selectedItems: selectedAssets, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, getItemId: (item) => item.value })) : (React.createElement("div", { className: "missing-information-tab__empty-state" }, translate('no_asset_exclusions', { namespace }))),
        React.createElement(AddAssetExclusionsDrawer, { isOpen: isDrawerOpen, onClose: () => setIsDrawerOpen(false), onAdd: handleAddAssets, existingAssetTypeValues: assetExclusions.map((a) => a.value), subtitle: subtitle }),
        deleteConfirmOpen && assetToDelete && (React.createElement(ConfirmDialog, { isOpen: deleteConfirmOpen, onConfirm: confirmDeleteAsset, onCancel: () => setDeleteConfirmOpen(false), title: translate('delete_asset_title', { namespace }), description: translate('delete_asset_description', { namespace, asset: assetToDelete.label }), confirmText: translate('delete', { namespace: 'features.commons' }), cancelText: translate('cancel', { namespace: 'features.commons' }) })),
        bulkDeleteConfirmOpen && (React.createElement(ConfirmDialog, { isOpen: bulkDeleteConfirmOpen, onConfirm: confirmBulkDelete, onCancel: () => setBulkDeleteConfirmOpen(false), title: translate('bulk_delete_assets_title', { namespace }), description: translate('bulk_delete_assets_description', { namespace, count: selectedAssets.length }), confirmText: translate('delete', { namespace: 'features.commons' }), cancelText: translate('cancel', { namespace: 'features.commons' }) }))));
}
